import React, { useCallback, useEffect, useState } from 'react';

import cn from 'classnames';
import { last, noop } from 'lodash';
import { bool, func, string } from 'prop-types';

import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import TranslationDrawer from 'components/Common/GoogleTranslateV2/components/TranslationDrawer';
import {
  DEFAULT_LANGUAGE_ENGLISH,
  EL_SCRIPT_SRC,
  GOOGLE_TRANSLATE_COOKIE_KEY,
  INITIAL_LANGUAGE,
  LANGUAGE_OPTIONS,
  SCRIPT_SELECTORS,
  SCRIPT_SRC,
  EL_LANGUAGE_OPTIONS,
} from 'components/Common/GoogleTranslateV2/constants';
import { handleSetLanguage } from 'components/Common/GoogleTranslateV2/utils';

import Select from 'components/Common/Select';
import { debounceIntervalLong } from 'constants/debounceInterval';
import useMediaInfo from 'hooks/useMediaInfo';
import useUserRedux from 'hooks/useUserRedux';
import { isNonEmptyArray } from 'utils/arrays';
import { loadScript } from 'utils/dom';

import s from './GoogleTranslate.css';

import './GoogleTranslateOverrides.css';

function initGoogleTranslate(isElementary) {
  const layoutCode = 0;
  const paramsBase = {
    pageLanguage: DEFAULT_LANGUAGE_ENGLISH,
    layout: layoutCode,
    autoDisplay: false,
    gaTrack: true,
    gaId: 'UA-43742130-1',
  };

  const allLanguages =
    'sq,am,ar,hy,bn,bs,bg,zh-CN,zh-TW,nl,en,tl,fr,de,el,gu,ht,iw,hi,hmn,ig,ilo,it,ja,km,ko,lo,ml,ne,ps,fa,pl,pt,pt-PT,pa,ro,ru,sr,sd,so,es,sw,ta,te,th,tr,uk,ur,vi,yo,rw,my,mn';
  const allParams = {
    ...paramsBase,
    includedLanguages: isElementary ? 'en,es' : allLanguages,
  };
  // eslint-disable-next-line no-new
  new window.google.translate.TranslateElement(
    allParams,
    'google_translate_element',
  );
}

function initGoogleTranslateElementary() {
  initGoogleTranslate(true);
}

window.initGoogleTranslateElementary = initGoogleTranslateElementary;
window.initGoogleTranslate = initGoogleTranslate;

/**
 * @param {string} className Class of the translate button
 * @param {boolean<false>} hidden If true, we hide the translate button from it's placement
 * @param {boolean<false>} isElementary If true, we load scripts correctly for elementary users
 * @param {boolean<false>} isOnboarding If true, we import different style set for the google translate button
 * @param {func} onClose Close translation drawer on tablet/mobile
 * @returns @GoogleTranslate
 */
const GoogleTranslate = ({
  className,
  hidden,
  isElementary,
  isOnboarding,
  onClose,
}) => {
  const { isDesktop } = useMediaInfo();
  const [cookies, setCookie, removeCookie] = useCookies([
    GOOGLE_TRANSLATE_COOKIE_KEY,
  ]);
  const selectedGoogleLanguage = cookies[GOOGLE_TRANSLATE_COOKIE_KEY];
  const { i18n } = useTranslation();
  const { isStudent, isCounselor, isGuardian, isOAuth2Authenticated } =
    useUserRedux();
  const getInitialLanguage = () => {
    if (selectedGoogleLanguage) {
      const googleLanguageKey = last(selectedGoogleLanguage.split('/'));
      return LANGUAGE_OPTIONS.find(
        option => option.value === googleLanguageKey,
      );
    }
    return INITIAL_LANGUAGE;
  };
  const [selectedLanguage, setSelectedLanguage] =
    useState(getInitialLanguage());

  const scriptSrc = isElementary ? EL_SCRIPT_SRC : SCRIPT_SRC;

  const reApplyScript = useCallback(() => {
    // Select the element that's embedded into DOM via script
    const scriptEl = document.querySelector(`script[src="${scriptSrc}"]`);

    // If element already exist, we remove the previously embedded element, to be able to create a new one
    if (scriptEl) {
      scriptEl.parentNode.removeChild(scriptEl);
    }

    // Iterate through script element selectors and remove previously embedded ones into DOM
    SCRIPT_SELECTORS.forEach(selector => {
      const elementToRemove = Array.prototype.slice.call(
        document.querySelectorAll(selector),
      );

      if (isNonEmptyArray(elementToRemove)) {
        elementToRemove.forEach(element => {
          element.parentNode.removeChild(element);
        });
      }
    });

    // Embed new element google translate element into DOM via the script
    loadScript(scriptSrc);
  }, [scriptSrc]);

  // we import correct css stylesheet depending on user type or if user is being onboarded
  useEffect(() => {
    if (isOnboarding) {
      import('components/Common/googleTranslateStyles/onboardingStyles.css');
    } else if (isStudent || isCounselor || isGuardian) {
      import(
        'components/Common/googleTranslateStyles/sideAndTopBarTranslateStyles.css'
      );
    } else if (isOAuth2Authenticated) {
      import(
        'components/Common/googleTranslateStyles/topBarTranslateStyles.css'
      );
    } else {
      import(
        'components/Common/googleTranslateStyles/defaultGoogleTranslateStyles.css'
      );
    }
  }, [isStudent, isCounselor, isGuardian, isOAuth2Authenticated, isOnboarding]);

  useEffect(() => {
    // Set a debounced loader by 1s, to be able to re-apply script to refresh translated items if they haven't.
    const debouncedLoader = setTimeout(() => {
      reApplyScript();
    }, debounceIntervalLong);

    // Clear the timeout when we close the google translator
    return () => clearTimeout(debouncedLoader);
  }, [hidden, reApplyScript, selectedGoogleLanguage]);

  // Tablet/mobile version will be handled separately
  const showDropdown = isDesktop && (!hidden || selectedGoogleLanguage);

  const onChange = val =>
    handleSetLanguage({
      val,
      i18n,
      setSelectedLanguage,
      removeCookie,
      setCookie,
      cookies,
      selectedLanguage,
    });

  const languageDrawerOpen = !hidden && !isDesktop;

  const languageOptions = isElementary ? EL_LANGUAGE_OPTIONS : LANGUAGE_OPTIONS;

  return (
    <>
      <div id="google_translate_element" className={cn(className, s.root)} />
      {showDropdown && (
        <Select
          classes={{
            inputRoot: s.selectInput,
            container: s.selectContainer,
            input: s.input,
          }}
          options={languageOptions}
          onChange={onChange}
          renderOption={option => <div>{option?.label}</div>}
          getOptionLabel={option => option?.label}
          getOptionSelected={(option, value) => option?.value === value?.value}
          variant="dropdown"
          disableClearable={selectedLanguage?.value === 'en'}
          value={selectedLanguage}
          noOptionsText="No matching languages"
          readOnlyInput
        />
      )}
      <TranslationDrawer
        isElementary={isElementary}
        languageDrawerOpen={languageDrawerOpen}
        languageOptions={languageOptions}
        onChange={onChange}
        onClose={onClose}
        selectedLanguage={selectedLanguage}
      />
    </>
  );
};

GoogleTranslate.propTypes = {
  className: string,
  hidden: bool,
  isElementary: bool,
  isOnboarding: bool,
  onClose: func,
};

GoogleTranslate.defaultProps = {
  className: undefined,
  hidden: false,
  isElementary: false,
  isOnboarding: false,
  onClose: noop,
};

export default GoogleTranslate;
